import { useState, useEffect } from 'react';
import { GroupedHistoryEntry, HistoryOccasion } from '../types';

interface HistoryTabProps {
  groupedHistory: GroupedHistoryEntry[];
  onViewWine: (canonicalWineId: string) => void;
  onAddFromHistory: (entry: GroupedHistoryEntry) => void;
  onDeleteOccasion: (occasion: HistoryOccasion) => void;
}

type SortType = 'most-enjoyed' | 'recent' | 'az';

export function HistoryTab({ 
  groupedHistory, 
  onViewWine, 
  onAddFromHistory,
  onDeleteOccasion 
}: HistoryTabProps) {
  const [sortBy, setSortBy] = useState<SortType>('most-enjoyed');
  const [selectedEntry, setSelectedEntry] = useState<GroupedHistoryEntry | null>(null);

  // Update selectedEntry when groupedHistory changes (e.g., after delete)
  useEffect(() => {
    if (selectedEntry) {
      // Find the updated entry in the new data
      const updatedEntry = groupedHistory.find(
        e => e.wine_name === selectedEntry.wine_name && 
             e.producer === selectedEntry.producer && 
             e.vintage === selectedEntry.vintage
      );
      
      if (!updatedEntry || updatedEntry.occasions.length === 0) {
        // Wine no longer exists or has no occasions - go back to list
        setSelectedEntry(null);
      } else if (updatedEntry.occasions.length !== selectedEntry.occasions.length) {
        // Occasions changed - update the selected entry
        setSelectedEntry(updatedEntry);
      }
    }
  }, [groupedHistory]);

  // Sort wines
  const sortedHistory = [...groupedHistory].sort((a, b) => {
    switch (sortBy) {
      case 'most-enjoyed':
        return b.total_consumed - a.total_consumed;
      case 'recent':
        const aDate = a.occasions[0]?.date_drank || '';
        const bDate = b.occasions[0]?.date_drank || '';
        return bDate.localeCompare(aDate);
      case 'az':
        return (a.wine_name || '').localeCompare(b.wine_name || '');
      default:
        return 0;
    }
  });

  // Calculate totals
  const totalBottles = groupedHistory.reduce((sum, e) => sum + e.total_consumed, 0);
  const uniqueWines = groupedHistory.length;

  const getWineColorClass = (color?: string) => {
    switch (color?.toLowerCase()) {
      case 'red': return 'wine-color-red';
      case 'white': return 'wine-color-white';
      case 'rosé': case 'rose': return 'wine-color-rose';
      case 'orange': return 'wine-color-orange';
      default: return 'wine-color-red';
    }
  };

  // Detail view for a specific wine's history
  if (selectedEntry) {
    return (
      <div className="main-content" style={{ background: 'var(--color-parchment)' }}>
        {/* Header */}
        <div style={{ 
          padding: '12px 16px 16px', 
          borderBottom: '1px solid var(--color-border)'
        }}>
          <button
            onClick={() => setSelectedEntry(null)}
            style={{ 
              background: 'none', 
              border: 'none', 
              color: 'var(--color-wine)', 
              fontSize: '15px',
              cursor: 'pointer',
              marginBottom: '8px',
              padding: 0
            }}
          >
            ← History
          </button>
          
          <h1 className="wine-title" style={{ 
            fontSize: '22px', 
            margin: '0 0 2px',
            color: 'var(--color-text)'
          }}>
            {selectedEntry.wine_name} {selectedEntry.vintage || ''}
          </h1>
          <p style={{ fontSize: '15px', color: 'var(--color-text-muted)', margin: 0 }}>
            {selectedEntry.producer}
          </p>
        </div>

        {/* Stats Banner */}
        <div style={{
          padding: '16px',
          background: 'var(--color-card)',
          display: 'flex',
          justifyContent: 'center',
          gap: '32px',
          borderBottom: '1px solid var(--color-border)'
        }}>
          <div style={{ textAlign: 'center' }}>
            <span className="wine-title" style={{ 
              fontSize: '28px', 
              color: 'var(--color-wine)',
              display: 'block'
            }}>
              {selectedEntry.total_consumed}
            </span>
            <span style={{ fontSize: '13px', color: 'var(--color-text-muted)' }}>
              bottles enjoyed
            </span>
          </div>
        </div>

        {/* Action Buttons */}
        <div style={{ 
          padding: '0 16px 16px',
          display: 'flex',
          gap: '8px'
        }}>
          {selectedEntry.canonical_wine_id && (
            <button
              onClick={() => onViewWine(selectedEntry.canonical_wine_id!)}
              className="ios-btn ios-btn-secondary"
              style={{ flex: 1 }}
            >
              View Wine
            </button>
          )}
          <button
            onClick={() => onAddFromHistory(selectedEntry)}
            className="ios-btn ios-btn-primary"
            style={{ flex: 1 }}
          >
            Add to Cellar
          </button>
        </div>

        {/* Timeline */}
        <div style={{ padding: '16px', paddingBottom: '100px' }}>
          <h3 style={{ 
            fontSize: '13px', 
            color: 'var(--color-text-muted)', 
            textTransform: 'uppercase',
            letterSpacing: '1px',
            margin: '0 0 12px'
          }}>
            Timeline
          </h3>
          
          {selectedEntry.occasions.length === 0 ? (
            <div className="ios-card animate-fade-in" style={{ padding: '24px', textAlign: 'center' }}>
              <p style={{ color: 'var(--color-text-muted)' }}>
                No occasions remaining
              </p>
            </div>
          ) : (
            selectedEntry.occasions.map((occasion, index) => (
              <div 
                key={occasion.id} 
                className="animate-fade-in"
                style={{
                  display: 'flex',
                  gap: '12px',
                  marginBottom: '16px'
                }}
              >
                {/* Timeline dot and line */}
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center',
                  paddingTop: '4px'
                }}>
                  <div style={{
                    width: '12px',
                    height: '12px',
                    borderRadius: '6px',
                    background: 'var(--color-wine)'
                  }} />
                  {index < selectedEntry.occasions.length - 1 && (
                    <div style={{
                      width: '2px',
                      flex: 1,
                      background: 'var(--color-border)',
                      marginTop: '4px'
                    }} />
                  )}
                </div>
                
                {/* Content */}
                <div className="ios-card" style={{ flex: 1, padding: '12px' }}>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: '6px'
                  }}>
                    <span style={{ 
                      fontSize: '15px', 
                      fontWeight: '600',
                      color: 'var(--color-text)'
                    }}>
                      {occasion.occasion_notes || 'Enjoyed'}
                    </span>
                    <span style={{ fontSize: '13px', color: 'var(--color-text-muted)' }}>
                      {new Date(occasion.date_drank).toLocaleDateString('en-US', { 
                        month: 'short', 
                        day: 'numeric', 
                        year: 'numeric' 
                      })}
                    </span>
                  </div>
                  
                  <div style={{ 
                    fontSize: '13px', 
                    color: 'var(--color-text-muted)',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <span>×{occasion.quantity_consumed} bottle{occasion.quantity_consumed > 1 ? 's' : ''}</span>
                    <button
                      onClick={() => onDeleteOccasion(occasion)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: 'var(--color-danger)',
                        fontSize: '12px',
                        cursor: 'pointer'
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    );
  }

  // Main list view
  return (
    <div className="main-content" style={{ background: 'var(--color-parchment)' }}>
      {/* Header */}
      <div className="ios-header">
        <h1 className="ios-header-title">History</h1>
        <p className="ios-header-subtitle">
          {totalBottles} bottles enjoyed · {uniqueWines} unique wines
        </p>
      </div>

      {/* Sort Pills */}
      <div style={{ 
        padding: '0 16px 12px',
        display: 'flex',
        gap: '8px'
      }}>
        {[
          { value: 'most-enjoyed', label: 'Most Enjoyed' },
          { value: 'recent', label: 'Recent' },
          { value: 'az', label: 'A-Z' }
        ].map(option => (
          <button
            key={option.value}
            onClick={() => setSortBy(option.value as SortType)}
            className={`ios-pill ${sortBy === option.value ? 'active' : ''}`}
          >
            {option.label}
          </button>
        ))}
      </div>

      {/* Wine List */}
      <div style={{
        padding: '0 16px',
        paddingBottom: '100px'
      }}>
        {sortedHistory.length === 0 ? (
          <div className="ios-card" style={{ padding: '32px', textAlign: 'center' }}>
            <p style={{ fontSize: '48px', marginBottom: '8px' }}>📜</p>
            <p style={{ color: 'var(--color-text-muted)' }}>
              No drinking history yet.<br />
              Start by enjoying a bottle from your cellar!
            </p>
          </div>
        ) : (
          <div className="ios-card" style={{ overflow: 'hidden' }}>
            {sortedHistory.map((entry) => (
              <div
                key={`${entry.wine_name}-${entry.vintage}`}
                className="ios-list-row"
                onClick={() => setSelectedEntry(entry)}
                style={{ cursor: 'pointer' }}
              >
                {/* Color bar */}
                <div 
                  className={`wine-color-bar ${getWineColorClass(entry.color)}`}
                  style={{ height: '40px' }}
                />
                
                {/* Wine Info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="wine-title" style={{ 
                    fontSize: '16px', 
                    color: 'var(--color-text)',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}>
                    {entry.wine_name}
                    {entry.vintage && (
                      <span style={{ 
                        fontWeight: '400', 
                        color: 'var(--color-text-muted)',
                        fontFamily: 'var(--font-body)'
                      }}> {entry.vintage}</span>
                    )}
                  </div>
                  <div style={{ 
                    fontSize: '13px', 
                    color: 'var(--color-text-muted)',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}>
                    Last enjoyed {new Date(entry.occasions[0]?.date_drank || '').toLocaleDateString('en-US', { 
                      month: 'short', 
                      day: 'numeric', 
                      year: 'numeric' 
                    })}
                  </div>
                </div>

                {/* Times Enjoyed Badge */}
                <div style={{
                  background: 'var(--color-wine)',
                  color: 'white',
                  borderRadius: '12px',
                  padding: '4px 10px',
                  fontSize: '13px',
                  fontWeight: '600'
                }}>
                  ×{entry.total_consumed}
                </div>

                {/* Chevron */}
                <span style={{ color: 'var(--color-border)', fontSize: '18px' }}>›</span>
              </div>
            ))}
          </div>
        )}

        <p style={{ 
          fontSize: '13px', 
          color: 'var(--color-text-muted)', 
          textAlign: 'center',
          marginTop: '12px'
        }}>
          Tap a wine to see full drinking history
        </p>
      </div>
    </div>
  );
}
